import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, CheckCircle, Upload, X, Calendar, Clock, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRICING = {
  clear_out: { single: 529, double: 699, large: 849 },
  reset: { single: 849, double: 1049, large: 1249 }
};

const ADD_ONS = {
  waste_removal_small: { name: "Waste Removal (Small Load)", price: 180 },
  waste_removal_large: { name: "Waste Removal (Large Load)", price: 220 },
  extra_labor: { name: "Extra Labor Hour (Clear-Out)", price: 95 },
  extra_labor_reset: { name: "Extra Labor Hour (Reset)", price: 120 }
};

const TIME_SLOTS = [
  "8:00 AM", "9:00 AM", "10:00 AM", "11:00 AM",
  "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM"
];

const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingId, setBookingId] = useState(null);

  // Purpose options for assessment
  const PURPOSE_OPTIONS = [
    { value: "car_space", label: "Want space for my car", recommendation: "Flex Flooring, Ceramic Coating" },
    { value: "workshop", label: "Create a workshop/workspace", recommendation: "LED Lighting, Electrical Outlets, Shelving" },
    { value: "storage", label: "Better organised storage", recommendation: "Storage Shelving" },
    { value: "personal_space", label: "Personal space / man cave", recommendation: "Flex Flooring, LED Lighting, Epoxy Flooring" },
    { value: "general_clean", label: "Just want it clean and tidy", recommendation: "Basic service" },
    { value: "selling_home", label: "Preparing to sell home", recommendation: "Epoxy Flooring, Carpet Cleaning" },
    { value: "other", label: "Other", recommendation: "" }
  ];

  // Form state
  const [formData, setFormData] = useState({
    // Assessment
    garage_size: "single",
    clutter_level: "medium",
    has_large_items: false,
    large_items_description: "",
    garage_purpose: "",
    service_type: searchParams.get("service") || "reset",
    wants_waste_removal: false,
    wants_enhancements: false,
    // Customer info
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    customer_address: "",
    // Booking
    add_ons: [],
    preferred_date: null,
    preferred_time: "",
    notes: "",
    photos: []
  });

  const [estimatedQuote, setEstimatedQuote] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  // Calculate quote whenever relevant fields change
  useEffect(() => {
    calculateQuote();
  }, [formData.service_type, formData.garage_size, formData.clutter_level, formData.wants_waste_removal, formData.add_ons]);

  const calculateQuote = () => {
    let basePrice = PRICING[formData.service_type]?.[formData.garage_size] || 529;
    
    // Clutter level adjustment
    const clutterMultiplier = { low: 1.0, medium: 1.15, high: 1.3 };
    basePrice *= clutterMultiplier[formData.clutter_level] || 1.0;
    
    // Add-ons
    let addOnTotal = 0;
    formData.add_ons.forEach(addOnId => {
      if (ADD_ONS[addOnId]) {
        addOnTotal += ADD_ONS[addOnId].price;
      }
    });
    
    // Waste removal
    if (formData.wants_waste_removal) {
      addOnTotal += 200;
    }
    
    setEstimatedQuote(Math.round(basePrice + addOnTotal));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    for (const file of files) {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      
      try {
        const response = await axios.post(`${API}/upload`, formDataUpload, {
          headers: { "Content-Type": "multipart/form-data" }
        });
        
        setUploadedFiles(prev => [...prev, {
          id: response.data.file_id,
          name: file.name,
          path: response.data.path
        }]);
        
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, response.data.path]
        }));
      } catch (error) {
        toast.error("Failed to upload file");
      }
    }
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => uploadedFiles[i]?.id !== fileId)
    }));
  };

  const handleAddOnToggle = (addOnId) => {
    setFormData(prev => ({
      ...prev,
      add_ons: prev.add_ons.includes(addOnId)
        ? prev.add_ons.filter(id => id !== addOnId)
        : [...prev.add_ons, addOnId]
    }));
  };

  const handleNext = () => {
    // Validation
    if (step === 1) {
      // Assessment validation
    } else if (step === 2) {
      // Service selection validation
    } else if (step === 3) {
      // Add-ons - no required fields
    } else if (step === 4) {
      if (!formData.customer_name || !formData.customer_email || !formData.customer_phone || !formData.customer_address) {
        toast.error("Please fill in all required fields");
        return;
      }
    } else if (step === 5) {
      if (!formData.preferred_date || !formData.preferred_time) {
        toast.error("Please select a date and time");
        return;
      }
    }
    
    setStep(prev => prev + 1);
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
  };

  const handleCreateBooking = async () => {
    setIsSubmitting(true);
    
    try {
      // Create assessment first
      const assessmentResponse = await axios.post(`${API}/assessments`, {
        garage_size: formData.garage_size,
        clutter_level: formData.clutter_level,
        has_large_items: formData.has_large_items,
        large_items_description: formData.large_items_description,
        garage_purpose: formData.garage_purpose,
        service_type: formData.service_type,
        wants_waste_removal: formData.wants_waste_removal,
        wants_enhancements: formData.wants_enhancements,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone
      });

      // Create booking
      const bookingResponse = await axios.post(`${API}/bookings`, {
        assessment_id: assessmentResponse.data.id,
        service_type: formData.service_type,
        garage_size: formData.garage_size,
        clutter_level: formData.clutter_level,
        wants_waste_removal: formData.wants_waste_removal,
        add_ons: formData.add_ons,
        preferred_date: format(formData.preferred_date, "yyyy-MM-dd"),
        preferred_time: formData.preferred_time,
        customer_name: formData.customer_name,
        customer_email: formData.customer_email,
        customer_phone: formData.customer_phone,
        customer_address: formData.customer_address,
        photos: formData.photos,
        notes: formData.notes
      });

      setBookingId(bookingResponse.data.id);
      // Use the SERVER-AUTHORITATIVE total on the review/payment screen —
      // this guarantees the customer sees the exact same number that is stored
      // in Mongo, charged by Stripe, and shown in the confirmation email.
      if (typeof bookingResponse.data.total_amount === "number") {
        setEstimatedQuote(bookingResponse.data.total_amount);
      }
      setStep(6); // Payment step
    } catch (error) {
      toast.error("Failed to create booking", {
        description: error.response?.data?.detail || "Please try again"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayment = async () => {
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/payments/create-checkout`, null, {
        params: {
          booking_id: bookingId,
          origin_url: window.location.origin
        }
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error("Failed to initiate payment", {
        description: error.response?.data?.detail || "Please try again"
      });
      setIsSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: "Assessment" },
    { number: 2, title: "Service" },
    { number: 3, title: "Add-Ons" },
    { number: 4, title: "Details" },
    { number: 5, title: "Schedule" },
    { number: 6, title: "Payment" }
  ];

  return (
    <div className="pt-20 min-h-screen bg-slate-50">
      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            {steps.map((s, index) => (
              <div key={s.number} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm ${
                  step >= s.number
                    ? "bg-[#1a365d] text-white"
                    : "bg-slate-200 text-slate-500"
                }`}>
                  {step > s.number ? <CheckCircle size={20} /> : s.number}
                </div>
                <span className={`ml-2 hidden sm:block text-sm font-medium ${
                  step >= s.number ? "text-[#0A0A0A]" : "text-slate-400"
                }`}>
                  {s.title}
                </span>
                {index < steps.length - 1 && (
                  <div className={`w-8 sm:w-16 h-1 mx-2 ${
                    step > s.number ? "bg-[#1a365d]" : "bg-slate-200"
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white p-8 border border-slate-200">
          {/* Step 1: Assessment */}
          {step === 1 && (
            <div className="space-y-8" data-testid="step-1-assessment">
              <div>
                <h2 className="heading-lg text-[#0A0A0A] mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Garage Assessment
                </h2>
                <p className="text-slate-600">Tell us about your garage to get an instant quote.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Garage Size</label>
                  <Select
                    value={formData.garage_size}
                    onValueChange={(value) => setFormData({ ...formData, garage_size: value })}
                  >
                    <SelectTrigger className="input-field" data-testid="garage-size-select">
                      <SelectValue placeholder="Select size" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="single">Single Garage</SelectItem>
                      <SelectItem value="double">Double Garage</SelectItem>
                      <SelectItem value="large">Large / Triple Garage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Clutter Level</label>
                  <Select
                    value={formData.clutter_level}
                    onValueChange={(value) => setFormData({ ...formData, clutter_level: value })}
                  >
                    <SelectTrigger className="input-field" data-testid="clutter-level-select">
                      <SelectValue placeholder="Select level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low - Mostly organised</SelectItem>
                      <SelectItem value="medium">Medium - Some clutter</SelectItem>
                      <SelectItem value="high">High - Very cluttered</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Purpose Question */}
              <div>
                <label className="block text-sm font-medium mb-2">What's the main purpose for cleaning out your garage?</label>
                <Select
                  value={formData.garage_purpose}
                  onValueChange={(value) => setFormData({ ...formData, garage_purpose: value })}
                >
                  <SelectTrigger className="input-field" data-testid="garage-purpose-select">
                    <SelectValue placeholder="Select your goal" />
                  </SelectTrigger>
                  <SelectContent>
                    {PURPOSE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.garage_purpose && formData.garage_purpose !== "general_clean" && formData.garage_purpose !== "other" && (
                  <p className="mt-2 text-sm text-[#1a365d] bg-blue-50 p-3 border-l-4 border-[#1a365d]">
                    <strong>Recommended upgrades:</strong> {PURPOSE_OPTIONS.find(o => o.value === formData.garage_purpose)?.recommendation}
                  </p>
                )}
              </div>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="large_items"
                  checked={formData.has_large_items}
                  onCheckedChange={(checked) => setFormData({ ...formData, has_large_items: checked })}
                  data-testid="has-large-items-checkbox"
                />
                <label htmlFor="large_items" className="text-sm">
                  I have large items (cupboards, shelving, furniture) that need removal
                </label>
              </div>

              {formData.has_large_items && (
                <div>
                  <label className="block text-sm font-medium mb-2">Describe large items</label>
                  <Textarea
                    value={formData.large_items_description}
                    onChange={(e) => setFormData({ ...formData, large_items_description: e.target.value })}
                    className="input-field min-h-[100px]"
                    placeholder="e.g., Old wardrobe, metal shelving unit..."
                    data-testid="large-items-description"
                  />
                </div>
              )}

              <div className="flex items-center gap-3">
                <Checkbox
                  id="waste_removal"
                  checked={formData.wants_waste_removal}
                  onCheckedChange={(checked) => setFormData({ ...formData, wants_waste_removal: checked })}
                  data-testid="wants-waste-removal-checkbox"
                />
                <label htmlFor="waste_removal" className="text-sm">
                  I need waste removal & tip disposal (+$180-$220)
                </label>
              </div>

              <div className="flex items-center gap-3">
                <Checkbox
                  id="enhancements"
                  checked={formData.wants_enhancements}
                  onCheckedChange={(checked) => setFormData({ ...formData, wants_enhancements: checked })}
                  data-testid="wants-enhancements-checkbox"
                />
                <label htmlFor="enhancements" className="text-sm">
                  I'm interested in space enhancements (flooring, lighting, shelving)
                </label>
              </div>
            </div>
          )}

          {/* Step 2: Service Selection */}
          {step === 2 && (
            <div className="space-y-8" data-testid="step-2-service">
              <div>
                <h2 className="heading-lg text-[#0A0A0A] mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Select Your Service
                </h2>
                <p className="text-slate-600">Choose the service that best fits your needs.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <button
                  onClick={() => setFormData({ ...formData, service_type: "clear_out" })}
                  className={`p-6 border-2 text-left transition-all ${
                    formData.service_type === "clear_out"
                      ? "border-[#1a365d] bg-blue-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid="select-clearout"
                >
                  <h3 className="heading-md text-[#0A0A0A] mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    Garage Clear-Out
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Fast removal of clutter and unwanted items
                  </p>
                  <p className="text-2xl font-bold text-[#1a365d]">
                    From ${PRICING.clear_out[formData.garage_size]}
                  </p>
                </button>

                <button
                  onClick={() => setFormData({ ...formData, service_type: "reset" })}
                  className={`p-6 border-2 text-left transition-all relative ${
                    formData.service_type === "reset"
                      ? "border-[#1a365d] bg-blue-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  data-testid="select-reset"
                >
                  <span className="absolute top-2 right-2 bg-[#1a365d] text-white px-2 py-1 text-xs font-bold">
                    POPULAR
                  </span>
                  <h3 className="heading-md text-[#0A0A0A] mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                    Garage Reset
                  </h3>
                  <p className="text-slate-600 text-sm mb-4">
                    Full organisation with sorting and structured layout
                  </p>
                  <p className="text-2xl font-bold text-[#1a365d]">
                    From ${PRICING.reset[formData.garage_size]}
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Add-Ons */}
          {step === 3 && (
            <div className="space-y-8" data-testid="step-3-addons">
              <div>
                <h2 className="heading-lg text-[#0A0A0A] mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Add-On Services
                </h2>
                <p className="text-slate-600">Optional extras to enhance your service.</p>
              </div>

              <div className="space-y-4">
                {Object.entries(ADD_ONS).map(([id, addon]) => (
                  <div
                    key={id}
                    className={`flex items-center justify-between p-4 border-2 cursor-pointer transition-all ${
                      formData.add_ons.includes(id)
                        ? "border-[#1a365d] bg-blue-50"
                        : "border-slate-200 hover:border-slate-300"
                    }`}
                    onClick={() => handleAddOnToggle(id)}
                    data-testid={`addon-${id}`}
                  >
                    <div className="flex items-center gap-4">
                      <Checkbox
                        checked={formData.add_ons.includes(id)}
                        onChange={() => {}}
                      />
                      <span className="font-medium">{addon.name}</span>
                    </div>
                    <span className="font-bold text-[#0A0A0A]">${addon.price}</span>
                  </div>
                ))}
              </div>

              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-medium mb-2">Upload Garage Photos (Optional)</label>
                <div className="border-2 border-dashed border-slate-300 p-8 text-center">
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="photo-upload"
                    data-testid="photo-upload-input"
                  />
                  <label
                    htmlFor="photo-upload"
                    className="cursor-pointer flex flex-col items-center"
                  >
                    <Upload size={40} className="text-slate-400 mb-2" />
                    <span className="text-slate-600">Click to upload photos</span>
                    <span className="text-slate-400 text-sm">Helps us provide accurate quotes</span>
                  </label>
                </div>

                {uploadedFiles.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {uploadedFiles.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center gap-2 bg-slate-100 px-3 py-2"
                      >
                        <span className="text-sm">{file.name}</span>
                        <button onClick={() => removeFile(file.id)}>
                          <X size={16} className="text-slate-500 hover:text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Customer Details */}
          {step === 4 && (
            <div className="space-y-8" data-testid="step-4-details">
              <div>
                <h2 className="heading-lg text-[#0A0A0A] mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Your Details
                </h2>
                <p className="text-slate-600">We need your contact information to confirm the booking.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Full Name *</label>
                  <Input
                    type="text"
                    required
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    className="input-field"
                    placeholder="John Smith"
                    data-testid="customer-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email *</label>
                  <Input
                    type="email"
                    required
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    className="input-field"
                    placeholder="john@example.com"
                    data-testid="customer-email-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Phone *</label>
                  <Input
                    type="tel"
                    required
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    className="input-field"
                    placeholder="0400 000 000"
                    data-testid="customer-phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Service Address *</label>
                <Textarea
                  required
                  value={formData.customer_address}
                  onChange={(e) => setFormData({ ...formData, customer_address: e.target.value })}
                  className="input-field min-h-[100px]"
                  placeholder="123 Example Street, Suburb, State, Postcode"
                  data-testid="customer-address-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Additional Notes (Optional)</label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="input-field min-h-[100px]"
                  placeholder="Any special requirements or access instructions..."
                  data-testid="notes-input"
                />
              </div>
            </div>
          )}

          {/* Step 5: Date Selection */}
          {step === 5 && (
            <div className="space-y-8" data-testid="step-5-schedule">
              <div>
                <h2 className="heading-lg text-[#0A0A0A] mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Select Date & Time
                </h2>
                <p className="text-slate-600">Choose your preferred appointment slot.</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <label className="block text-sm font-medium mb-2">Preferred Date *</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal input-field"
                        data-testid="date-picker-trigger"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {formData.preferred_date ? (
                          format(formData.preferred_date, "PPP")
                        ) : (
                          <span className="text-slate-400">Pick a date</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={formData.preferred_date}
                        onSelect={(date) => setFormData({ ...formData, preferred_date: date })}
                        disabled={(date) => date < new Date()}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Preferred Time *</label>
                  <Select
                    value={formData.preferred_time}
                    onValueChange={(value) => setFormData({ ...formData, preferred_time: value })}
                  >
                    <SelectTrigger className="input-field" data-testid="time-select">
                      <SelectValue placeholder="Select time">
                        <div className="flex items-center">
                          <Clock className="mr-2 h-4 w-4" />
                          {formData.preferred_time || "Select time"}
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Payment */}
          {step === 6 && (
            <div className="space-y-8" data-testid="step-6-payment">
              <div>
                <h2 className="heading-lg text-[#0A0A0A] mb-2" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
                  Confirm & Pay Deposit
                </h2>
                <p className="text-slate-600">Review your booking and pay the $100 deposit to confirm.</p>
              </div>

              {/* Booking Summary */}
              <div className="bg-slate-50 p-6 space-y-4">
                <h3 className="font-bold uppercase tracking-wider text-sm border-b border-slate-200 pb-2">
                  Booking Summary
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-600">Service</span>
                    <span className="font-medium">
                      {formData.service_type === "clear_out" ? "Garage Clear-Out" : "Garage Reset"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Garage Size</span>
                    <span className="font-medium capitalize">{formData.garage_size}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Date</span>
                    <span className="font-medium">
                      {formData.preferred_date ? format(formData.preferred_date, "PPP") : "-"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600">Time</span>
                    <span className="font-medium">{formData.preferred_time}</span>
                  </div>
                  {formData.add_ons.length > 0 && (
                    <div className="pt-2 border-t border-slate-200">
                      <span className="text-slate-600 text-sm">Add-Ons:</span>
                      <ul className="text-sm mt-1">
                        {formData.add_ons.map((id) => (
                          <li key={id} className="flex justify-between">
                            <span>{ADD_ONS[id]?.name}</span>
                            <span>${ADD_ONS[id]?.price}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="pt-4 border-t border-slate-300">
                  <div className="flex justify-between text-lg">
                    <span className="font-bold">Estimated Total</span>
                    <span className="font-bold text-[#1a365d]">${estimatedQuote}</span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    Final price may vary based on actual work required
                  </p>
                </div>
              </div>

              {/* Deposit Info */}
              <div className="bg-blue-50 border-l-4 border-[#1a365d] p-4">
                <div className="flex items-center gap-3">
                  <CreditCard className="text-[#1a365d]" size={24} />
                  <div>
                    <p className="font-bold text-[#0A0A0A]">Deposit Required: $100 AUD</p>
                    <p className="text-sm text-slate-600">
                      Remaining balance of ${estimatedQuote - 100} due after service completion
                    </p>
                  </div>
                </div>
              </div>

              {/* Customer Info Summary */}
              <div className="bg-slate-50 p-6">
                <h3 className="font-bold uppercase tracking-wider text-sm mb-4">Contact Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Name:</span>
                    <p className="font-medium">{formData.customer_name}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Email:</span>
                    <p className="font-medium">{formData.customer_email}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Phone:</span>
                    <p className="font-medium">{formData.customer_phone}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Address:</span>
                    <p className="font-medium">{formData.customer_address}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Quote Display */}
          {step < 6 && (
            <div className="mt-8 p-4 bg-slate-100 flex justify-between items-center">
              <span className="font-medium">Estimated Quote</span>
              <span className="text-2xl font-bold text-[#1a365d]" data-testid="estimated-quote">
                ${estimatedQuote}
              </span>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="mt-8 flex justify-between">
            {step > 1 && step <= 6 && (
              <Button
                variant="outline"
                onClick={handleBack}
                className="btn-secondary"
                data-testid="back-btn"
              >
                <ArrowLeft className="mr-2" size={18} />
                Back
              </Button>
            )}
            
            {step < 5 && (
              <Button
                onClick={handleNext}
                className="btn-primary ml-auto"
                data-testid="next-btn"
              >
                Continue
                <ArrowRight className="ml-2" size={18} />
              </Button>
            )}

            {step === 5 && (
              <Button
                onClick={handleCreateBooking}
                className="btn-primary ml-auto"
                disabled={isSubmitting}
                data-testid="create-booking-btn"
              >
                {isSubmitting ? "Creating..." : "Continue to Payment"}
                <ArrowRight className="ml-2" size={18} />
              </Button>
            )}

            {step === 6 && (
              <Button
                onClick={handlePayment}
                className="btn-primary ml-auto"
                disabled={isSubmitting}
                data-testid="pay-deposit-btn"
              >
                {isSubmitting ? "Processing..." : "Pay $100 Deposit"}
                <CreditCard className="ml-2" size={18} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
